import { FC, ReactElement, useMemo, useState } from "react";
import {
  Box,
  Card,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  Button,
  styled,
} from "@mui/material";
import { Public, Code, BusinessCenter, KeyboardArrowDown, KeyboardArrowRight, FileDownloadOutlined } from "@mui/icons-material";
import { handleCSVDownload, numberWithCommas } from "@/utils";
import { responsiveFontSizes, pxToRem } from "@/utils/responsive";
import { NoData, HTSCodeWithTooltip } from "@/components";
import { AnalyticsTableHeader, AnalyticsTableFooter } from "@/components/table/analytics-table-chrome";
import { COUNTRIES } from "@/constants";
import { getCountryFlag } from "@/constants/country-flags";
import { tokens } from "@/tokens";

/* Data-viz accent color (not in the design-token palette) */
const CHART_BLUE = "#0E7090";

const VendorTableWrapper = styled(Box)(({ theme }) => ({
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  maxHeight: 440,
  [theme.breakpoints.down("md")]: {
    maxHeight: 440,
  },
  "&::-webkit-scrollbar": {
    width: 4,
    height: 4,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: tokens.scrollbar.thumb,
    borderRadius: 4,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: tokens.scrollbar.thumbHover,
  },
  "& .MuiTable-root": {
    borderCollapse: "collapse",
    "& .MuiTableCell-root": {
      border: 0,
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      backgroundColor: `${tokens.bg.surface} !important`,
      "&:hover": { backgroundColor: `${tokens.bg.hover} !important` },
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: `${pxToRem(7)} ${pxToRem(12)} !important`,
      borderBlock: `1px solid ${tokens.border.default}`,
    },
  },
}));

type TabType = "country" | "tariff" | "vendor";

const TABS: { id: TabType; label: string; icon: typeof Public }[] = [
  { id: "country", label: "Origin", icon: Public },
  { id: "tariff", label: "HTS Code", icon: Code },
  { id: "vendor", label: "Supplier", icon: BusinessCenter },
];

/* ------------------------------------------------------------------ */
/*  Footer section configs (static, no mutation risk)                  */
/* ------------------------------------------------------------------ */

const ORIGIN_FOOTER: VendorSection[] = [
  { type: "total", name: "Total" },
  { type: "field", fieldName: "customsValue", symbol: "$" },
  { type: "field", fieldName: "customsDuty", symbol: "$" },
  { type: "field", fieldName: "distinctImports", symbol: "" },
  { type: "field", fieldName: "effectiveDutyRate", symbol: "" },
];

const TARIFF_FOOTER: VendorSection[] = [
  { type: "total", name: "Total" },
  { type: "field", fieldName: "tariffValue", symbol: "$" },
  { type: "field", fieldName: "tariffDuty", symbol: "$" },
  { type: "field", fieldName: "percentOfTotal", symbol: "" },
  { type: "field", fieldName: "effectiveDutyRate", symbol: "" },
];

const VENDOR_FOOTER: VendorSection[] = [
  { type: "total", name: "Total" },
  { type: "field", fieldName: "customsValue", symbol: "$" },
  { type: "field", fieldName: "customsDuty", symbol: "$" },
  { type: "field", fieldName: "percentCustomValue", symbol: "%" },
];

/* ------------------------------------------------------------------ */
/*  Shared column cell helpers                                         */
/* ------------------------------------------------------------------ */

const cellFont = { xs: "9px", md: "10px", lg: "11px" };
const ellipsis = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } as const;

const CurrencyCell: FC<{ value: number }> = ({ value }) => {
  const label = `$${numberWithCommas(value)}`;
  return (
    <Tooltip title={label} arrow>
      <Typography variant="body2" sx={{ fontSize: cellFont, ...ellipsis }}>
        {label}
      </Typography>
    </Tooltip>
  );
};

const DutyRateBar: FC<{ rate: number }> = ({ rate }) => {
  const barColor = rate < 5 ? "#10B981" : rate < 10 ? "#F59E0B" : "#EF4444";
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography
        variant="body2"
        fontWeight={tokens.fontWeight.bold}
        sx={{ fontSize: cellFont, minWidth: 36 }}
      >
        {rate.toFixed(1)}%
      </Typography>
      <Box sx={{ flex: 1, height: 4, bgcolor: "rgba(0,0,0,0.06)", borderRadius: 2, overflow: "hidden", maxWidth: 48 }}>
        <Box sx={{ height: "100%", width: `${Math.min(rate, 100)}%`, bgcolor: barColor, borderRadius: 2 }} />
      </Box>
    </Stack>
  );
};

/* ------------------------------------------------------------------ */
/*  Expandable Origin Row                                              */
/* ------------------------------------------------------------------ */

const HTS_SUB_COLUMNS: IColumn<IHtsNumber & { exportCountry: string }>[] = [
  {
    name: "HTS Code",
    key: "id" as any,
    align: "left",
    width: "30%",
    minWidth: "140px",
    component: (data) => (
      <Box sx={{ pl: 4 }}>
        <HTSCodeWithTooltip htsCode={data.id.toString()}>
          <Typography
            variant="body2"
            sx={{
              fontSize: cellFont,
              cursor: "pointer",
              "&:hover": { color: CHART_BLUE, textDecoration: "underline" },
            }}
          >
            {data.id}
          </Typography>
        </HTSCodeWithTooltip>
      </Box>
    ),
  },
  {
    name: "Import Value",
    key: "customValue" as any,
    align: "left",
    width: "22%",
    minWidth: "90px",
    component: (data) => <CurrencyCell value={data.customValue} />,
  },
  {
    name: "Duties",
    key: "customDuty" as any,
    align: "left",
    width: "18%",
    minWidth: "80px",
    component: (data) => <CurrencyCell value={data.customDuty} />,
  },
  {
    name: "Entries",
    key: "distinctImports" as any,
    align: "center",
    width: "12%",
    minWidth: "60px",
  },
  {
    name: "Duty Rate",
    key: "effectiveDutyRate" as any,
    align: "left",
    width: "18%",
    minWidth: "100px",
    component: (data) => <DutyRateBar rate={data.effectiveDutyRate * 100} />,
  },
];

const ExpandableOriginRow: FC<{
  country: CountryData;
  columns: IColumn<CountryData>[];
  htsData: (IHtsNumber & { exportCountry: string })[];
}> = ({ country, columns, htsData }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: "pointer", "&:hover": { bgcolor: tokens.bg.muted } }}
      >
        {columns.map((col) => (
          <TableCell
            key={`${String(col.key)}-${country._id}`}
            align={col.align}
            sx={{
              width: col.width,
              minWidth: col.minWidth || "auto",
              borderBottom: expanded ? "0 !important" : undefined,
            }}
          >
            {col.key === "_id" ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <IconButton size="small" sx={{ p: 0.25 }}>
                  {expanded ? (
                    <KeyboardArrowDown sx={{ fontSize: 16, color: CHART_BLUE }} />
                  ) : (
                    <KeyboardArrowRight sx={{ fontSize: 16, color: tokens.text.muted }} />
                  )}
                </IconButton>
                {col.component ? col.component(country) : country._id}
              </Stack>
            ) : col.component ? (
              col.component(country)
            ) : (
              country[col.key as keyof CountryData]
            )}
          </TableCell>
        ))}
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={columns.length} sx={{ p: "0 !important", border: "0 !important" }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableBody>
                  {htsData.map((hts, idx) => (
                    <TableRow key={`${hts.id}-${idx}`} sx={{ "&:hover": { bgcolor: tokens.bg.muted } }}>
                      {HTS_SUB_COLUMNS.map((col) => (
                        <TableCell
                          key={`${String(col.key)}-${hts.id}`}
                          align={col.align}
                          sx={{ width: col.width, minWidth: col.minWidth || "auto" }}
                        >
                          {col.component
                            ? col.component(hts)
                            : hts[col.key as keyof (IHtsNumber & { exportCountry: string })]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

interface UnifiedAnalyticsCardProps {
  importExportData: {
    exports: IExport[];
    _id: string;
  }[];
  originFooterData: Record<string, number>;
  tariffData: ITariff[];
  tariffIsLoading: boolean;
  vendorsData: IVendor[];
}

const UnifiedAnalyticsCard: FC<UnifiedAnalyticsCardProps> = ({
  importExportData,
  originFooterData,
  tariffData,
  tariffIsLoading,
  vendorsData,
}): ReactElement => {
  const [activeTab, setActiveTab] = useState<TabType>("country");

  /* ---------------------------------------------------------------- */
  /*  Column definitions — useMemo guarantees fresh arrays per render  */
  /* ---------------------------------------------------------------- */

  const originColumns = useMemo<IColumn<CountryData>[]>(() => [
    {
      name: "Country of Origin",
      key: "_id",
      align: "left",
      width: "30%",
      minWidth: "140px",
      component: (data) => {
        const country = COUNTRIES.find((c) => c.shortForm === data._id);
        const name = country?.name ?? data._id;
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <span style={{ fontSize: "16px", lineHeight: 1 }}>{getCountryFlag(data._id)}</span>
            <Typography variant="body2" whiteSpace="nowrap" sx={{ fontSize: cellFont }}>
              {name}
            </Typography>
          </Stack>
        );
      },
    },
    {
      name: "Import Value",
      key: "customValue",
      align: "left",
      width: "22%",
      minWidth: "90px",
      component: ({ customValue }) => <CurrencyCell value={customValue} />,
    },
    {
      name: "Duties",
      key: "customDuty",
      align: "left",
      width: "18%",
      minWidth: "80px",
      component: ({ customDuty }) => <CurrencyCell value={customDuty} />,
    },
    {
      name: "Entries",
      key: "distinctImports",
      align: "center",
      width: "12%",
      minWidth: "60px",
    },
    {
      name: "Duty Rate",
      key: "effectiveDutyRate",
      align: "left",
      width: "18%",
      minWidth: "100px",
      component: ({ effectiveDutyRate }) => <DutyRateBar rate={effectiveDutyRate * 100} />,
    },
  ], []);

  const tariffColumns = useMemo<IColumn<ITariff & { percentOfTotal?: number }>[]>(() => [
    {
      name: "HTS Code",
      key: "htsNumber",
      align: "left",
      width: "30%",
      minWidth: "140px",
      component: (data) => {
        const htsCode = data.htsNumber == null ? "" : String(data.htsNumber);
        const cell = (
          <Typography
            variant="body2"
            sx={{
              fontSize: cellFont,
              ...ellipsis,
              cursor: htsCode ? "pointer" : "default",
              ...(htsCode && { "&:hover": { color: "#0b494b", textDecoration: "underline" } }),
            }}
          >
            {htsCode || "\u2014"}
          </Typography>
        );
        return htsCode ? <HTSCodeWithTooltip htsCode={htsCode}>{cell}</HTSCodeWithTooltip> : cell;
      },
    },
    {
      name: "Import Value",
      key: "tariffValue",
      align: "left",
      width: "22%",
      minWidth: "90px",
      component: ({ tariffValue }) => <CurrencyCell value={tariffValue || 0} />,
    },
    {
      name: "Duties",
      key: "tariffDuty",
      align: "left",
      width: "18%",
      minWidth: "80px",
      component: ({ tariffDuty }) => <CurrencyCell value={tariffDuty || 0} />,
    },
    {
      name: "% of Total",
      key: "percentOfTotal" as any,
      align: "center",
      width: "12%",
      minWidth: "60px",
      component: (data) => (
        <Typography variant="body2" sx={{ fontSize: cellFont }}>
          {((data as any).percentOfTotal || 0).toFixed(1)}%
        </Typography>
      ),
    },
    {
      name: "Duty Rate",
      key: "effectiveDutyRate" as any,
      align: "left",
      width: "18%",
      minWidth: "100px",
      component: (data) => {
        const tariffValue = data.tariffValue || 0;
        const tariffDuty = data.tariffDuty || 0;
        const rate = tariffValue > 0 ? (tariffDuty / tariffValue) * 100 : 0;
        return <DutyRateBar rate={rate} />;
      },
    },
  ], []);

  const vendorColumns = useMemo<IColumn<IVendor>[]>(() => [
    {
      name: "Supplier",
      key: "manufacturerName",
      align: "left",
      width: "30%",
      minWidth: "140px",
      component: ({ manufacturerName }) => (
        <Typography variant="body2" sx={{ fontSize: cellFont, ...ellipsis }}>
          {manufacturerName || "N/A"}
        </Typography>
      ),
    },
    {
      name: "Import Value",
      key: "tariffValue",
      align: "left",
      width: "22%",
      minWidth: "90px",
      component: ({ tariffValue }) => <CurrencyCell value={tariffValue} />,
    },
    {
      name: "Duties",
      key: "tariffDuty",
      align: "left",
      width: "18%",
      minWidth: "80px",
      component: ({ tariffDuty }) => <CurrencyCell value={tariffDuty} />,
    },
    {
      name: "Duty Rate",
      key: "customValue",
      align: "left",
      width: "18%",
      minWidth: "100px",
      component: ({ customValue }) => <DutyRateBar rate={customValue * 100} />,
    },
  ], []);

  /* ---------------------------------------------------------------- */
  /*  Data processing                                                  */
  /* ---------------------------------------------------------------- */

  const { importExportTopLevel, htsDataByCountry } = useMemo(() => {
    const topLevel: CountryData[] = [];
    const htsMap: Record<string, (IHtsNumber & { exportCountry: string })[]> = {};

    importExportData.forEach((country) => {
      const topLevelDistinctSupplierValues = new Set();
      const topLevelDistinctImportValues = new Set();
      let topLevelCustomValue = 0;
      let topLevelCustomDuty = 0;
      let topLevelEffectiveDutyRate = 0;
      let topLevelDutySaved = 0;
      let totalHtsCount = 0;

      const countryHts: (IHtsNumber & { exportCountry: string })[] = [];

      country.exports.forEach((exportItem) => {
        exportItem.htsNumbers.forEach((hts) => {
          topLevelCustomValue += hts.customValue;
          topLevelCustomDuty += hts.customDuty;
          topLevelEffectiveDutyRate += hts.effectiveDutyRate;
          topLevelDutySaved += hts.dutySaved;
          hts.distinctSuppliersValues.forEach((supplier: string) =>
            topLevelDistinctSupplierValues.add(supplier)
          );
          hts.distinctImportsValues.forEach((importValue: string) =>
            topLevelDistinctImportValues.add(importValue)
          );
          totalHtsCount++;
          countryHts.push({ ...hts, exportCountry: exportItem._id });
        });
      });

      topLevelEffectiveDutyRate =
        totalHtsCount > 0 ? topLevelEffectiveDutyRate / totalHtsCount : 0;

      if (country._id) {
        topLevel.push({
          _id: country._id,
          customValue: topLevelCustomValue,
          customDuty: topLevelCustomDuty,
          distinctImports: topLevelDistinctImportValues.size,
          distinctSuppliers: topLevelDistinctSupplierValues.size,
          effectiveDutyRate: topLevelEffectiveDutyRate,
          avgShipmentValue:
            topLevelDistinctImportValues.size > 0
              ? topLevelCustomValue / topLevelDistinctImportValues.size
              : 0,
          dutySaved: topLevelDutySaved,
        } as CountryData);
        htsMap[country._id] = countryHts;
      }
    });

    return { importExportTopLevel: topLevel, htsDataByCountry: htsMap };
  }, [importExportData]);

  const {
    tariffValue,
    tariffDuty,
    percentOfTotal,
    effectiveDutyRate: tariffEffectiveDutyRate,
    enrichedTariffData,
  } = useMemo(() => {
    if (!tariffData) {
      return { tariffValue: 0, tariffDuty: 0, percentOfTotal: 100, effectiveDutyRate: 0, enrichedTariffData: [] };
    }

    const totalValue = tariffData.reduce((sum, item) => sum + (item.tariffValue || 0), 0);
    const totalDuty = tariffData.reduce((sum, item) => sum + (item.tariffDuty || 0), 0);
    const effectiveDutyRate = totalValue > 0 ? (totalDuty / totalValue) * 100 : 0;

    const enriched = tariffData.map((item) => ({
      ...item,
      percentOfTotal: totalValue > 0 ? ((item.tariffValue || 0) / totalValue) * 100 : 0,
    }));

    return { tariffValue: totalValue, tariffDuty: totalDuty, percentOfTotal: 100, effectiveDutyRate, enrichedTariffData: enriched };
  }, [tariffData]);

  const { vendorsCustomsDuty, vendorsCustomsValue, vendorsPercentCustomValue } = useMemo(() => {
    const customsDuty = vendorsData.reduce((prev, { tariffDuty }) => prev + tariffDuty, 0);
    const customsValue = vendorsData.reduce((prev, { tariffValue }) => prev + tariffValue, 0);
    const percentCustomValue = customsValue ? (customsDuty / customsValue) * 100 : 0;
    return { vendorsCustomsDuty: customsDuty, vendorsCustomsValue: customsValue, vendorsPercentCustomValue: percentCustomValue };
  }, [vendorsData]);

  /* ---------------------------------------------------------------- */
  /*  Render helpers                                                   */
  /* ---------------------------------------------------------------- */

  const tableSx = { tableLayout: "fixed" as const, width: "100%", borderCollapse: "collapse" as const };

  /** Generic flat-row table renderer used by tariff & vendor tabs */
  const renderFlatTable = <T,>(
    columns: IColumn<T>[],
    data: T[],
    rowKey: (item: T) => string,
    footerSections: VendorSection[],
    footerData: Record<string, number>,
    totalLabel?: string,
  ) => (
    <VendorTableWrapper>
      <Table stickyHeader sx={tableSx}>
        <AnalyticsTableHeader columns={columns} />
        <TableBody>
          {data.map((item) => (
            <TableRow key={rowKey(item)}>
              {columns.map((col) => (
                <TableCell
                  key={String(col.key)}
                  align={col.align}
                  sx={{ width: col.width, minWidth: col.minWidth || "auto" }}
                >
                  {col.component ? col.component(item as any) : (item as any)[col.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        <AnalyticsTableFooter
          sections={footerSections}
          data={footerData}
          columns={columns}
          totalLabel={totalLabel}
        />
      </Table>
    </VendorTableWrapper>
  );

  const renderDownloadButton = () => {
    if (activeTab === "country") return null;

    const handleClick = () => {
      if (activeTab === "tariff") {
        if (!tariffData) return;
        const headers = ["HTS Code", "Goods Value", "Customs Duty", "Effective Duty Rate (%)"];
        const csvData = tariffData.map((r) => [
          r.htsNumber,
          r.tariffValue ?? 0,
          r.tariffDuty ?? 0,
          (r.tariffValue ?? 0) > 0
            ? (((r.tariffDuty ?? 0) / (r.tariffValue ?? 1)) * 100).toFixed(2)
            : "0.00",
        ]);
        handleCSVDownload(headers, csvData, "tariff_codes.csv");
      } else if (activeTab === "vendor") {
        const headers = ["Manufacturer", "Manufacturer ID", "Customs Value", "Customs Duty", "Effective Duty Rate (%)"];
        const csvData = vendorsData.map((r) => [
          r.manufacturerName ?? r.manufacturer,
          r.manufacturer,
          `$${Number(r.tariffValue ?? 0)}`,
          `$${Number(r.tariffDuty ?? 0)}`,
          (r.tariffValue ?? 0) > 0
            ? (((r.tariffDuty ?? 0) / (r.tariffValue ?? 1)) * 100).toFixed(2)
            : "0.00",
        ]);
        handleCSVDownload(headers, csvData, "manufacturers.csv");
      }
    };

    return (
      <Button
        size="small"
        variant="text"
        onClick={handleClick}
        startIcon={<FileDownloadOutlined sx={{ fontSize: "14px !important" }} />}
        sx={{
          fontSize: tokens.fontSize.xs,
          fontWeight: tokens.fontWeight.semibold,
          color: tokens.text.secondary,
          px: 1,
          py: 0.5,
          minWidth: "auto",
          "&:hover": { color: CHART_BLUE, bgcolor: `${CHART_BLUE}0F` },
        }}
      >
        Export
      </Button>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "country": {
        if (importExportData.length === 0) return <NoData />;
        return (
          <VendorTableWrapper>
            <Table stickyHeader sx={tableSx}>
              <AnalyticsTableHeader columns={originColumns} />
              <TableBody>
                {importExportTopLevel.map((country) => (
                  <ExpandableOriginRow
                    key={country._id}
                    country={country}
                    columns={originColumns}
                    htsData={htsDataByCountry[country._id] || []}
                  />
                ))}
              </TableBody>
              <AnalyticsTableFooter
                sections={ORIGIN_FOOTER}
                data={originFooterData}
                columns={originColumns}
                totalLabel={`Total (${importExportTopLevel.length})`}
              />
            </Table>
          </VendorTableWrapper>
        );
      }
      case "tariff": {
        if (enrichedTariffData.length === 0 && !tariffIsLoading) return <NoData />;
        return renderFlatTable(
          tariffColumns,
          enrichedTariffData,
          (item) => String(item.htsNumber),
          TARIFF_FOOTER,
          { tariffValue, tariffDuty, effectiveDutyRate: tariffEffectiveDutyRate, percentOfTotal },
        );
      }
      case "vendor": {
        if (vendorsData.length === 0) return <NoData />;
        return renderFlatTable(
          vendorColumns,
          vendorsData,
          (item) => item.manufacturer,
          VENDOR_FOOTER,
          { customsDuty: vendorsCustomsDuty, customsValue: vendorsCustomsValue, percentCustomValue: vendorsPercentCustomValue },
        );
      }
      default:
        return null;
    }
  };

  return (
    <Card sx={{ p: { xs: 1.5, md: 2 }, height: "auto", minHeight: "auto", border: `1px solid ${tokens.border.default}`, boxShadow: "none" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
        <Box>
          <Typography
            color={tokens.text.primary}
            fontWeight={tokens.fontWeight.bold}
            sx={{ fontSize: responsiveFontSizes.lg, lineHeight: 1.3 }}
          >
            Import Breakdown
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: tokens.text.secondary, fontSize: responsiveFontSizes.xs, mt: 0.25 }}
          >
            Detailed view by Origin, HTS Code, or Supplier
          </Typography>
        </Box>
        {renderDownloadButton()}
      </Stack>

      <Stack direction="row" spacing={0} sx={{ borderBottom: `1px solid ${tokens.border.default}`, mb: 1.5 }}>
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const IconComponent = tab.icon;
          return (
            <Box
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 2,
                py: 1,
                cursor: "pointer",
                borderBottom: isActive ? "2px solid #0a0a0f" : "2px solid transparent",
                color: isActive ? "#0a0a0f" : tokens.text.secondary,
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  color: isActive ? "#0a0a0f" : tokens.text.body,
                },
              }}
            >
              <IconComponent sx={{ fontSize: 16 }} />
              <Typography
                sx={{
                  fontSize: responsiveFontSizes.sm,
                  fontWeight: isActive ? tokens.fontWeight.bold : tokens.fontWeight.semibold,
                }}
              >
                {tab.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {renderContent()}
    </Card>
  );
};

export default UnifiedAnalyticsCard;
